import React, { useState } from 'react';
import { Plus, Users, Building2, UserCheck, Cpu, Briefcase, UserPlus, LayoutGrid, History, Car, Tag, FileText, ClipboardCheck, UserCog, Package, PawPrint, KeyRound, BookOpen, CalendarDays, ScrollText, AlertTriangle, Megaphone, Badge }  from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useIsMobile } from '../../../hooks/useMediaQuery';
import { BottomNav } from '../../ui/BottomNav';

interface ActionMenuBarProps {
  currentView: 'visitors' | 'cadastro' | 'history' | 'location-detail' | 'person-profile' | 'register-visitor' | 'register-resident' | 'expired-visitors' | 'device-audit' | 'encomendas' | 'eventos-gerais' | 'zone-occupancy' | 'pre-registrations' | 'areas-comuns' | 'normas-internas' | 'ocorrencias' | 'mural-anuncios' | 'avisos' | 'help-center' | 'manuais' | 'livro' | 'solicitacoes';
  setCurrentView: (view: 'visitors' | 'cadastro' | 'history' | 'location-detail' | 'person-profile' | 'register-visitor' | 'register-resident' | 'expired-visitors' | 'device-audit' | 'encomendas' | 'eventos-gerais' | 'zone-occupancy' | 'pre-registrations' | 'areas-comuns' | 'normas-internas' | 'ocorrencias' | 'mural-anuncios' | 'avisos' | 'help-center' | 'manuais') => void;
  cadastrosMenuOpen: boolean;
  setCadastrosMenuOpen: (open: boolean) => void;
  setSelectedCadastro: (cadastro: string) => void;
  onRegisterVisitor?: () => void;
  customerType?: 'condominio' | 'empresa';
  onShowAddNote?: () => void;
  onShowNotesHistory?: () => void;
  activeNotesCount?: number;
  onShowPreRegistrations?: () => void;
  preRegistrationsCount?: number;
  onShowAccountRequests?: () => void;
  accountRequestsCount?: number;
  onShowEncomendas?: () => void;
  encomendaPendingCount?: number;
  petsEnabled?: boolean;
  keysEnabled?: boolean;
  livroEnabled?: boolean;
  onShowLivro?: () => void;
  solicitacoesEnabled?: boolean;
  onShowSolicitacoes?: () => void;
  solicitacoesPendingCount?: number;
  commonAreasEnabled?: boolean;
  onShowAreasComuns?: () => void;
  normasInternasEnabled?: boolean;
  onShowNormas?: () => void;
  ocorrenciasEnabled?: boolean;
  onShowOcorrencias?: () => void;
  muralAnunciosEnabled?: boolean;
  onShowMuralAnuncios?: () => void;
  lightTheme?: boolean;
}

const ActionMenuBarComponent: React.FC<ActionMenuBarProps> = ({
  currentView,
  setCurrentView,
  cadastrosMenuOpen,
  setCadastrosMenuOpen,
  setSelectedCadastro,
  onRegisterVisitor,
  customerType = 'condominio',
  onShowAddNote,
  onShowNotesHistory,
  activeNotesCount = 0,
  onShowPreRegistrations,
  preRegistrationsCount = 0,
  onShowAccountRequests,
  accountRequestsCount = 0,
  onShowEncomendas,
  encomendaPendingCount = 0,
  petsEnabled = false,
  keysEnabled = false,
  livroEnabled = false,
  onShowLivro,
  solicitacoesEnabled = false,
  onShowSolicitacoes,
  solicitacoesPendingCount = 0,
  commonAreasEnabled = false,
  onShowAreasComuns,
  normasInternasEnabled = false,
  onShowNormas,
  ocorrenciasEnabled = false,
  onShowOcorrencias,
  muralAnunciosEnabled = false,
  onShowMuralAnuncios,
  lightTheme = false,
}) => {
  const isMobile = useIsMobile();
  const [mobileCadastrosOpen, setMobileCadastrosOpen] = useState(false);

  // F1 keyboard shortcut for Visitantes Ativos
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F1') {
        e.preventDefault();
        setCurrentView('visitors');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setCurrentView]);

  // Define menu items based on customer type
  const cadastroItems = customerType === 'empresa'
    ? [
        { id: 'funcionarios', label: 'Funcionários', icon: Users },
        { id: 'departamentos', label: 'Departamentos', icon: Briefcase },
        { id: 'visitantes', label: 'Visitantes', icon: UserCheck },
        { id: 'prestadores', label: 'Prestadores', icon: ClipboardCheck },
        { id: 'veiculos', label: 'Veículos', icon: Car },
        { id: 'tags', label: 'Tags de Acesso', icon: Tag },
        { id: 'dispositivos', label: 'Dispositivos', icon: Cpu },
        { id: 'encomendas', label: 'Encomendas', icon: Package },
        ...(keysEnabled ? [{ id: 'chaves', label: 'Chaves', icon: KeyRound }] : []),
        ...(solicitacoesEnabled ? [{ id: 'solicitacoes', label: 'Solicitações', icon: Badge, badge: solicitacoesPendingCount }] : []),
        ...(normasInternasEnabled ? [{ id: 'normas', label: 'Normas', icon: ScrollText }] : []),
        ...(ocorrenciasEnabled ? [{ id: 'ocorrencias', label: 'Ocorrências', icon: AlertTriangle }] : []),
        ...(muralAnunciosEnabled ? [{ id: 'mural', label: 'Mural', icon: Megaphone }] : []),
        { id: 'relatorios', label: 'Relatórios', icon: FileText },
      ]
    : [
        { id: 'moradores', label: 'Moradores', icon: Users },
        { id: 'unidades', label: 'Unidades', icon: Building2 },
        { id: 'visitantes', label: 'Visitantes', icon: UserCheck },
        { id: 'prestadores', label: 'Prestadores', icon: ClipboardCheck },
        { id: 'veiculos', label: 'Veículos', icon: Car },
        ...(petsEnabled ? [{ id: 'animais', label: 'Pets', icon: PawPrint }] : []),
        { id: 'tags', label: 'Tags de Acesso', icon: Tag },
        { id: 'dispositivos', label: 'Dispositivos', icon: Cpu },
        { id: 'encomendas', label: 'Encomendas', icon: Package },
        ...(keysEnabled ? [{ id: 'chaves', label: 'Chaves', icon: KeyRound }] : []),
        ...(commonAreasEnabled ? [{ id: 'reservas', label: 'Reservas', icon: CalendarDays }] : []),
        ...(solicitacoesEnabled ? [{ id: 'solicitacoes', label: 'Solicitações', icon: Badge, badge: solicitacoesPendingCount }] : []),
        ...(normasInternasEnabled ? [{ id: 'normas', label: 'Normas', icon: ScrollText }] : []),
        ...(ocorrenciasEnabled ? [{ id: 'ocorrencias', label: 'Ocorrências', icon: AlertTriangle }] : []),
        ...(muralAnunciosEnabled ? [{ id: 'mural', label: 'Mural', icon: Megaphone }] : []),
        { id: 'relatorios', label: 'Relatórios', icon: FileText },
      ];

  const handleCadastroClick = (id: string) => {
    if (id === 'encomendas') {
      onShowEncomendas?.();
      setCadastrosMenuOpen(false);
      return;
    }
    if (id === 'chaves') {
      setCadastrosMenuOpen(false);
      setCurrentView('cadastro');
      setSelectedCadastro('chaves');
      return;
    }
    if (id === 'reservas') {
      onShowAreasComuns?.();
      setCadastrosMenuOpen(false);
      return;
    }
    // These three open their own full panels rather than a cadastro list.
    const panelOpeners: Record<string, (() => void) | undefined> = {
      solicitacoes: onShowSolicitacoes,
      normas: onShowNormas,
      ocorrencias: onShowOcorrencias,
      mural: onShowMuralAnuncios,
    };
    if (panelOpeners[id]) {
      panelOpeners[id]?.();
      setCadastrosMenuOpen(false);
      return;
    }
    setCurrentView('cadastro');
    setSelectedCadastro(id);
  };

  // Mobile: Render as bottom navigation
  if (isMobile) {
    return (
      <>
        <BottomNav
          lightTheme={lightTheme}
          items={[
            {
              id: 'visitors',
              label: 'Ativos (F1)',
              icon: <Users className="h-5 w-5" />,
              active: currentView === 'visitors',
              action: () => setCurrentView('visitors'),
            },
            {
              id: 'cadastros',
              label: 'Cadastros',
              icon: <LayoutGrid className="h-5 w-5" />,
              active: currentView === 'cadastro',
              action: () => setMobileCadastrosOpen(true),
            },
          ]}
        />

        {/* Mobile cadastros modal/sheet */}
        {mobileCadastrosOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-end"
            onClick={() => setMobileCadastrosOpen(false)}
          >
            <div
              className="w-full bg-gray-950 rounded-t-2xl border-t border-gray-800 max-h-[70vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-gray-950 border-b border-gray-800 px-4 py-3">
                <h3 className="text-lg font-bold text-white">Cadastros</h3>
              </div>

              {/* Menu items */}
              <div className="p-2">
                {cadastroItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        handleCadastroClick(item.id);
                        setMobileCadastrosOpen(false);
                      }}
                      className="w-full px-4 py-3 text-left text-gray-300 hover:bg-gray-900 active:bg-gray-800 rounded-lg transition-colors"
                    >
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop: Render as top bar
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => setCurrentView('visitors')}
        className={cn(
          "px-2 py-1 md:px-4 md:py-1.5 text-xs md:text-sm font-bold transition-all border flex items-center gap-2 text-[white]",
          currentView === 'visitors'
            ? "rounded-tl-lg rounded-tr-lg rounded-bl-none rounded-br-none border-b-0 bg-gradient-to-r from-blue-600 to-blue-700 border-blue-500 shadow-lg shadow-blue-500/20 relative z-10"
            : "rounded-lg bg-gradient-to-r from-blue-600/80 to-blue-700/80 hover:from-blue-600 hover:to-blue-700 border-blue-600 shadow-md hover:shadow-lg hover:ring-2 hover:ring-blue-500/20"
        )}
        title="Visitantes Ativos (F1)"
      >
        <Users className="h-4 w-4" />
        Visitantes Ativos
        {currentView !== 'visitors' && (
          <kbd className="hidden md:inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold bg-white/20 border border-white/30 rounded text-[white]">
            F1
          </kbd>
        )}
      </button>

      {/* Cadastros Dropdown - Shows on hover */}
      <div
        className="relative"
        onMouseEnter={() => setCadastrosMenuOpen(true)}
        onMouseLeave={() => setCadastrosMenuOpen(false)}
      >
        <button
          className={cn(
            "px-2 py-1 md:px-4 md:py-1.5 rounded-lg text-xs md:text-sm font-semibold transition-all border flex items-center gap-2",
            currentView === 'cadastro'
              ? lightTheme
                ? "bg-slate-200 border-slate-400 text-slate-800 ring-1 ring-slate-400/30"
                : "bg-slate-800 border-slate-600 text-white ring-1 ring-slate-500/20"
              : lightTheme
                ? "bg-white hover:bg-gray-100 border-gray-300 text-gray-700"
                : "bg-gray-900 hover:bg-gray-800 border-gray-800 text-gray-200"
          )}
        >
          Cadastros
          <svg className={cn("h-3 w-3 transition-transform", cadastrosMenuOpen && "rotate-180")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {cadastrosMenuOpen && (
          <div className="absolute top-full pt-1 left-0 z-50" data-testid="cadastros-menu">
            <div className={cn("w-48 rounded-lg shadow-2xl border overflow-hidden", lightTheme ? "bg-white border-gray-200" : "bg-gray-950 border-gray-800")}>
              {cadastroItems.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => handleCadastroClick(item.id)}
                    className={cn(
                      "w-full px-4 py-2 text-left text-sm transition-colors flex items-center justify-between gap-2",
                      lightTheme
                        ? "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                        : "text-gray-300 hover:bg-gray-900 hover:text-white",
                      index > 0 && (lightTheme ? "border-t border-gray-100" : "border-t border-gray-800")
                    )}
                  >
                    <span>{item.label}</span>
                    {!!(item as { badge?: number }).badge && (
                      <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-orange-500 text-white leading-none">
                        {(item as { badge?: number }).badge}
                      </span>
                    )}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Spacer to push buttons to the right */}
      <div className="flex-1" />

      {/* Pre-Registrations Button — always visible; badge shows the pending count */}
      {onShowPreRegistrations && (
        <button
          onClick={onShowPreRegistrations}
          className={cn(
            "flex items-center gap-1.5 px-2 py-1 md:px-3 md:py-1.5 rounded-lg text-xs md:text-sm font-semibold transition-all border",
            currentView === 'pre-registrations'
              ? lightTheme
                ? "bg-blue-100 border-blue-400 text-blue-700 ring-1 ring-blue-400/30"
                : "bg-blue-900/30 border-blue-600 text-blue-300 ring-1 ring-blue-500/20"
              : lightTheme
                ? "bg-white hover:bg-gray-50 text-gray-700 border-gray-300 hover:border-gray-400"
                : "bg-gray-900 hover:bg-gray-800 text-gray-300 border-gray-800 hover:border-gray-700"
          )}
          title="Pré-Registros Pendentes"
        >
          <span>Pré-Registros</span>
          {preRegistrationsCount > 0 && (
            <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-orange-500 text-white ml-1 leading-none">
              {preRegistrationsCount}
            </span>
          )}
        </button>
      )}

      {/* Notes Buttons */}
      {onShowAddNote && onShowNotesHistory && (
        <div className="flex items-center gap-1.5">
          <button
            onClick={onShowAddNote}
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 md:px-3 md:py-1.5 rounded-lg text-xs md:text-sm font-semibold transition-all border",
              lightTheme
                ? "bg-white hover:bg-gray-50 text-gray-700 border-gray-300 hover:border-gray-400"
                : "bg-gray-900 hover:bg-gray-800 text-gray-300 border-gray-800 hover:border-gray-700"
            )}
            title="Novo Aviso"
          >
            <span>Novo Aviso</span>
            {activeNotesCount > 0 && (
              <span className={cn(
                "text-xs font-bold px-1.5 py-0.5 rounded ml-1",
                lightTheme ? "bg-gray-800 text-white" : "bg-gray-600 text-white"
              )}>
                {activeNotesCount}
              </span>
            )}
          </button>
          <button
            onClick={onShowNotesHistory}
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 md:px-2 md:py-1.5 rounded-lg text-xs md:text-sm font-semibold transition-all border",
              lightTheme
                ? "bg-white hover:bg-gray-50 text-gray-500 hover:text-gray-700 border-gray-300 hover:border-gray-400"
                : "bg-gray-900 hover:bg-gray-800 text-gray-400 hover:text-gray-200 border-gray-800 hover:border-gray-700"
            )}
            title="Histórico de Avisos"
          >
            <History className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Livro de Ocorrências Button */}
      {livroEnabled && onShowLivro && (
        <button
          onClick={onShowLivro}
          className={cn(
            "flex items-center gap-1.5 px-2 py-1 md:px-3 md:py-1.5 rounded-lg text-xs md:text-sm font-semibold transition-all border",
            currentView === 'livro'
              ? lightTheme
                ? "bg-indigo-100 border-indigo-400 text-indigo-700 ring-1 ring-indigo-400/30"
                : "bg-indigo-900/30 border-indigo-600 text-indigo-300 ring-1 ring-indigo-500/20"
              : lightTheme
                ? "bg-white hover:bg-gray-50 text-gray-700 border-gray-300 hover:border-gray-400"
                : "bg-gray-900 hover:bg-gray-800 text-gray-300 border-gray-800 hover:border-gray-700"
          )}
          title="Livro de Ocorrências"
        >
          <BookOpen className="h-4 w-4" />
          <span>Livro</span>
        </button>
      )}

      {/* Solicitações / Normas / Ocorrências / Mural now live in the Cadastros menu — the top
          bar was overflowing and pushing the search boxes off a porteiro's screen. */}

    </div>
  );
};


// Wrap in React.memo for performance
export const ActionMenuBar = React.memo(ActionMenuBarComponent);
